import { useState, useEffect, useRef, useCallback } from "react";
import GoodTarget from "@/assets/img/testExplorationGoodTarget.png";
import BadTarget from "@/assets/img/testExplorationBadTarget.png";

type Target = {
  position: string;
  isTarget: boolean;
};

type TrialData = {
  trialNumber: number;
  positions: Target[];
  timeTaken: number; // Time in milliseconds
  clickedTargetPosition: string; // Original position (e.g., "A12")
  success: boolean;
};


type Props = {
  caseSize: number;
  totalTrials: number;
  onTrialEnd: (trialData: TrialData) => void; // Callback for trial end
  onTestEnd: (trialDataSuccess : boolean) => void; // Callback for test end
};

const ExplorationTest = ({
  caseSize,
  totalTrials,
  onTrialEnd,
  onTestEnd,
}: Props) => {
  const [currentTrial, setCurrentTrial] = useState(0);
  const [displayCross, setDisplayCross] = useState(true);
  const displayCrossRef = useRef<boolean>(true);
  const [targets, setTargets] = useState<Target[]>([]);

  const trialStartTimeRef = useRef<number>(0);

  const generateRandomPositions = (count: number): string[] => {
    const positions = new Set<string>();
    const letters = "ABCDEFGHIJKLMNOP";
    const numbers = Array.from({ length: 12 }, (_, i) => i + 1);

    while (positions.size < count) {
      const letter = letters.charAt(Math.floor(Math.random() * letters.length));
      const number = numbers[Math.floor(Math.random() * numbers.length)];
      positions.add(`${letter}${number}`);
    }

    return Array.from(positions);
  };

  const generateTrial = useCallback(() => {
    const distractorPositions = generateRandomPositions(30);
    const targetPosition = generateRandomPositions(1)[0];

    const distractors = distractorPositions.filter(
      (pos) => pos !== targetPosition
    );

    const trialTargets = [
      ...distractors.map((pos) => ({ position: pos, isTarget: false })),
      { position: targetPosition, isTarget: true },
    ];

    trialTargets.sort(() => Math.random() - 0.5);
    setTargets(trialTargets);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: { key: string }) => {
      if (e.key === " " && displayCrossRef.current) {
        setDisplayCross(false); // Update the state to hide the cross
        displayCrossRef.current = false;
        generateTrial(); // Generate the targets after cross is hidden
        trialStartTimeRef.current = Date.now(); // Start the timer for the next trial
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [generateTrial]);

  useEffect(() => {
    // This effect handles the end of the test
    if (currentTrial === totalTrials) {
      onTestEnd(false); // All trials completed
    }
  }, [currentTrial, totalTrials, onTestEnd]);

const handleClick = (target: Target) => {
  const trialEndTime = Date.now();
  const timeTaken = trialEndTime - trialStartTimeRef.current; // Calculate time taken

  // Calculate the coordinates of the clicked target
  const trialData: TrialData = {
    trialNumber: currentTrial + 1,
    positions: targets,
    timeTaken,
    clickedTargetPosition: target.position, // Add the original position (e.g., "A12")
    success: target.isTarget,
  };

  onTrialEnd(trialData); // Pass the trial data to the parent

  if (target.isTarget) {
    onTestEnd(true); // End the test if the correct target is found
  } else {
    setCurrentTrial((prev) => prev + 1); // Move to the next trial if the wrong target is clicked
  }
};


  const { canvasWidth, canvasHeight } = calculateCanvasDimensions(caseSize);

  function calculateCanvasDimensions(caseSize: number) {
    const maxColumns = 16;
    const maxRows = 12;
    const canvasWidth = maxColumns * caseSize;
    const canvasHeight = maxRows * caseSize;
    return { canvasWidth, canvasHeight };
  }

  const convertGridPositionToCoordinates = (position: string) => {
    const letter = position.charAt(0).toUpperCase();
    const number = parseInt(position.slice(1), 10);
    const col = letter.charCodeAt(0) - 65;
    const row = number - 1;
    const x = col * caseSize;
    const y = row * caseSize;
    return { x, y };
  };

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          backgroundColor: "white",
        }}
      >
        {displayCross && (
          <div
            style={{
              position: "absolute",
              top: canvasHeight / 2 - 10,
              left: canvasWidth / 2 - 10,
              width: (0.7 * caseSize) / 4,
              height: (0.7 * caseSize) / 4,
              color: "black",
              fontSize: 20,
            }}
          >
            +
          </div>
        )}

        {!displayCross &&
          targets.map((target, index) => {
            const { x, y } = convertGridPositionToCoordinates(target.position);
            const targetImage = target.isTarget ? GoodTarget : BadTarget;

            return (
              <img
                key={index}
                onClick={() => handleClick(target)}
                src={targetImage}
                alt={target.isTarget ? "target" : "distractor"}
                style={{
                  position: "absolute",
                  top: y,
                  left: x,
                  width: caseSize / 4,
                  height: caseSize / 4,
                  cursor: "pointer",
                }}
              />
            );
          })}
      </div>
    </div>
  );
};

export default ExplorationTest;
